import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from '../AuthContext';
import Swal from "sweetalert2";
import "./AdminEditProductVariant.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AdminEditProductVariant() {
  const { id } = useParams();
  const { authFetch } = useContext(AuthContext);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [variants, setVariants] = useState([]);
  const [specifications, setSpecifications] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/products/${id}`);
        if (!response.ok) {
          throw new Error("Failed to fetch product");
        }
        const data = await response.json();
        setProduct(data);
        setVariants(
          (data.variants || []).map((variant) => ({
            ...variant,
            variantValueIds:
              variant.variantValues?.map((sv) => sv.specValueId) || [],
            price: variant.price,
            bundleQuantity: variant.bundleQuantity,
            uom: variant.uom,
            moq: variant.moq,
            isExisting: true
          }))
        );
        setSpecifications(
          (data.specifications || []).map((spec) => ({
            ...spec,
            values: spec.specificationValues.map((sv) => sv.value),
          }))
        );
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  // Validate if all variants have complete specification values
  const areVariantsValid = () => {
    if (variants.length === 0) return true;

    return variants.every((variant) => {
      // Check if this variant has a value for each specification
      return specifications.every((spec, index) => {
        return (
          variant.variantValueIds &&
          variant.variantValueIds[index] !== undefined &&
          variant.variantValueIds[index] !== ""
        );
      });
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!areVariantsValid()) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Variants",
        text: "Please ensure all variants have complete specification values before saving.",
        confirmButtonColor: "#45a049",
      });
      return;
    }

    const originalVariants = (product.variants || []).map((variant) => ({
      variantId: variant.variantId,
      variantValueIds: variant.variantValues?.map((vv) => vv.specValueId) || [],
      price: variant.price,
      bundleQuantity: variant.bundleQuantity,
      uom: variant.uom,
      moq: variant.moq
    }));

    // Detect if any variants have changes
    const variantsChanged =
    originalVariants.length !== variants.length ||
    JSON.stringify(originalVariants.map((v) => ({
      values: [...v.variantValueIds].sort(),
      price: v.price,
      bundleQuantity: v.bundleQuantity ?? null,
      uom: v.uom ?? "",
      moq: v.moq ?? null
    }))) !==
    JSON.stringify(variants.map((v) => ({
      values: [...v.variantValueIds].sort(),
      price: v.price,
      bundleQuantity: v.bundleQuantity ?? null,
      uom: v.uom ?? "",
      moq: v.moq ?? null
    })));

    if (!variantsChanged) {
      Swal.fire({
        icon: "info",
        title: "No Changes",
        text: "No changes detected to product variants.",
        confirmButtonColor: "#3498db",
      });
      return;
    }

    setIsSaving(true);

    try {
      if (
        variants.length === 0 &&
        product.variants &&
        product.variants.length > 0
      ) {
        const result = await Swal.fire({
          icon: "warning",
          title: "Remove All Variants",
          text: "Are you sure you want to remove all variants from this product?",
          showCancelButton: true,
          confirmButtonColor: "#e74c3c",
          cancelButtonColor: "#7a7a7a",
          confirmButtonText: "Yes",
          cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) {
          setIsSaving(false);
          return;
        }

        try {
          Swal.fire({
            title: "Removing Variants",
            text: "Please wait...",
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });
          for (const variant of product.variants) {
            const deleteResponse = await authFetch(
              `${API_BASE_URL}/products/variant/${variant.variantId}`,
              {
                method: "DELETE",
              }
            );

            if (!deleteResponse.ok) {
              throw new Error(`Failed to delete variant: ${variant.variantId}`);
            }
          }

          Swal.fire({
            icon: "success",
            title: "Success!",
            text: "All variants have been removed successfully!",
            confirmButtonColor: "#45a049",
            showConfirmButton: true,
          }).then(() => {
            navigate(`/admin/edit-product/${id}`);
          });
          return;
        } catch (error) {
          setError(error);
          Swal.fire({
            icon: "error",
            title: "Removal Failed",
            text: "Failed to remove variants: " + error.message,
            confirmButtonColor: "#e74c3c",
          });
          setIsSaving(false);
          return;
        }
      }

      Swal.fire({
        title: "Saving Variants",
        text: "Please wait...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      const formData = new FormData();

      formData.append("Name", product.name);
      formData.append("Description", product.description || "");
      if (product.price !== null && product.price !== undefined) {
        formData.append("Price", product.price);
      }      
      formData.append("CategoryID", product.categoryID);
      if (product.batchStockQuantity !== null && product.batchStockQuantity !== undefined) {
        formData.append("BatchStockQuantity", product.batchStockQuantity);
      }
      if (product.stockQuantity !== null && product.stockQuantity !== undefined) {
        formData.append("StockQuantity", product.stockQuantity);
      }
      formData.append("MOQ", product.moq ?? "");
      formData.append("ProductCode", product.productCode || "");

      formData.append("UpdateSpecifications", "false");
      formData.append("UpdateVariants", "true");

      variants.forEach((variant, variantIndex) => {
        if (variant.variantId) {
          formData.append(
            `Variants[${variantIndex}].VariantId`,
            variant.variantId
          );
        }

        if (variant.price !== null && variant.price !== undefined && variant.price !== "") {
          formData.append(
            `Variants[${variantIndex}].Price`,
            variant.price
          );
        }

        // Bundle Qty / UOM / MOQ are all optional - only send when provided
        if (variant.bundleQuantity !== null && variant.bundleQuantity !== undefined && variant.bundleQuantity !== "") {
          formData.append(
            `Variants[${variantIndex}].BundleQuantity`,
            variant.bundleQuantity
          );
        }

        if (variant.uom !== null && variant.uom !== undefined && variant.uom !== "") {
          formData.append(
            `Variants[${variantIndex}].UOM`,
            variant.uom
          );
        }

        if (variant.moq !== null && variant.moq !== undefined && variant.moq !== "") {
          formData.append(
            `Variants[${variantIndex}].MOQ`,
            variant.moq
          );
        }

        variant.variantValueIds.forEach((specValueId, valueIndex) => {
          formData.append(
            `Variants[${variantIndex}].SpecificationValueIds[${valueIndex}]`,
            specValueId
          );
        });
      });

      const response = await authFetch(`${API_BASE_URL}/products/${id}`, {
        method: "PUT",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to update product");
      }

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Product variants updated successfully!",
        confirmButtonColor: "#45a049",
        showConfirmButton: true,
      }).then(() => {
        window.location.reload();
      });
    } catch (error) {
      setError(error);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: "Failed to update product: " + error.message,
        confirmButtonColor: "#e74c3c",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleRemoveVariant = (index) => {
    const newVariants = [...variants];
    newVariants.splice(index, 1);
    setVariants(newVariants);
  };

  const handleAddVariant = () => {
    setVariants([
      ...variants,
      {
        variantValueIds: [],
        price: null,
        bundleQuantity: null,
        uom: "",
        moq: null,
        isExisting: false
      }
    ]);
  };

  if (loading) {
    return (
      <div className="admin-edit-product-variant-loading">
        <div className="admin-edit-product-variant-loading-spinner">
          <i className="fas fa-spinner fa-spin"></i>
        </div>
        <p>Loading product variants...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-edit-product-variant-container">
        <div className="admin-edit-product-variant-error">
          <i className="fas fa-exclamation-circle"></i> Error: {error.message}
        </div>
        <button
          className="admin-edit-product-variant-back-btn"
          onClick={handleBack}
        >
          <i className="fas fa-arrow-left"></i> Back
        </button>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="admin-edit-product-variant-container">
        <div className="admin-edit-product-variant-error">
          <i className="fas fa-exclamation-triangle"></i> Product not found
        </div>
        <button
          className="admin-edit-product-variant-back-btn"
          onClick={handleBack}
        >
          <i className="fas fa-arrow-left"></i> Back
        </button>
      </div>
    );
  }

  return (
    <div className="admin-edit-product-variant-container">
      <div className="admin-edit-product-variant-header">
        <button
          className="admin-edit-product-variant-back-btn"
          onClick={handleBack}
        >
          <i className="fas fa-arrow-left"></i> Back
        </button>
      </div>
      <div className="admin-edit-product-variant-title">
        <h1>Edit Product Variants</h1>
        <p className="admin-edit-product-variant-subtitle">
          <i className="fas fa-info-circle"></i> Configure product variations
           for<strong>{product.name}</strong>
        </p>
      </div>
      <form onSubmit={handleSubmit} className="admin-edit-product-variant-form">
        <div className="admin-edit-product-variant-instructions">
          <h3>
            <i className="fas fa-tags"></i> Variants
          </h3>
          <p>
            Each variant represents a unique combination of product
            specifications (e.g., Color: Red, Size: Large). Add all combinations
            that are available for this product.
          </p>
        </div>
        <div className="admin-edit-product-variant-table-container">
          <table className="admin-edit-product-variant-table">
            <thead>
              <tr>
                <th className="admin-edit-product-variant-table-header">
                  Variant #
                </th>
                {specifications.map((spec, idx) => (
                  <th
                    key={idx}
                    className="admin-edit-product-variant-table-header"
                  >
                    {spec.name}
                  </th>
                ))}
                <th className="admin-edit-product-variant-table-header">
                  Price (RM) (optional)
                </th>
                <th className="admin-edit-product-variant-table-header">
                  Bundle Qty (optional)
                </th>
                <th className="admin-edit-product-variant-table-header">
                  UOM (optional)
                </th>
                <th className="admin-edit-product-variant-table-header">
                  MOQ (optional)
                </th>
                <th className="admin-edit-product-variant-table-header">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {variants.map((variant, index) => (
                <tr
                  key={index}
                  className="admin-edit-product-variant-table-row"
                >
                  <td className="admin-edit-product-variant-table-cell admin-edit-product-variant-variant-number">
                    {index + 1}
                  </td>
                  {/* Specification value cells */}
                  {specifications.map((specification, specIndex) => {
                    const specValueId = variant.variantValueIds[specIndex];
                    const specValue = specification.specificationValues?.find(
                      (sv) => sv.specificationValueId === specValueId
                    );

                    return (
                      <td
                        key={specIndex}
                        className="admin-edit-product-variant-table-cell"
                      >
                        {variant.isExisting ? (
                          /* Existing variant - display value */
                          <span className="admin-edit-product-variant-spec-value">
                            {specValue ? specValue.value : "N/A"}
                          </span>
                        ) : (
                          /* New variant - show dropdown */
                          <select
                            className="admin-edit-product-variant-spec-select"
                            value={variant.variantValueIds?.[specIndex] || ""}
                            onChange={(e) => {
                              const newVariants = [...variants];
                              const newVariantValueIds = [
                                ...(newVariants[index].variantValueIds || []),
                              ];
                              newVariantValueIds[specIndex] = parseInt(
                                e.target.value
                              );
                              newVariants[index] = {
                                ...newVariants[index],
                                variantValueIds: newVariantValueIds,
                              };
                              setVariants(newVariants);
                            }}
                          >
                            <option value="">
                              Select {specification.name}
                            </option>
                            {specification.specificationValues?.map(
                              (specValue) => (
                                <option
                                  key={specValue.specificationValueId}
                                  value={specValue.specificationValueId}
                                >
                                  {specValue.value}
                                </option>
                              )
                            )}
                          </select>
                        )}
                      </td>
                    );
                  })}

                  {/* Price cell */}
                  <td className="admin-edit-product-variant-table-cell">
                    <input
                      type="number"
                      step="0.5"
                      className="admin-edit-product-variant-price-input"
                      value={variant.price || ""}
                      onChange={(e) => {
                        const newVariants = [...variants];
                        newVariants[index] = {
                          ...newVariants[index],
                          price: e.target.value === "" ? null : parseFloat(e.target.value)
                        };
                        setVariants(newVariants);
                      }}
                    />
                  </td>

                  {/* Bundle Quantity cell (optional) - base product units this variant consumes */}
                  <td className="admin-edit-product-variant-table-cell">
                    <input
                      type="number"
                      min="1"
                      placeholder="e.g. 192"
                      className="admin-edit-product-variant-price-input"
                      value={variant.bundleQuantity ?? ""}
                      onChange={(e) => {
                        const newVariants = [...variants];
                        newVariants[index] = {
                          ...newVariants[index],
                          bundleQuantity: e.target.value === "" ? null : parseInt(e.target.value, 10)
                        };
                        setVariants(newVariants);
                      }}
                    />
                  </td>

                  {/* UOM cell (optional) */}
                  <td className="admin-edit-product-variant-table-cell">
                    <input
                      type="text"
                      placeholder="e.g. bags"
                      className="admin-edit-product-variant-price-input"
                      value={variant.uom ?? ""}
                      onChange={(e) => {
                        const newVariants = [...variants];
                        newVariants[index] = {
                          ...newVariants[index],
                          uom: e.target.value
                        };
                        setVariants(newVariants);
                      }}
                    />
                  </td>

                  {/* MOQ cell (optional) */}
                  <td className="admin-edit-product-variant-table-cell">
                    <input
                      type="number"
                      min="1"
                      placeholder="e.g. 1"
                      className="admin-edit-product-variant-price-input"
                      value={variant.moq ?? ""}
                      onChange={(e) => {
                        const newVariants = [...variants];
                        newVariants[index] = {
                          ...newVariants[index],
                          moq: e.target.value === "" ? null : parseInt(e.target.value, 10)
                        };
                        setVariants(newVariants);
                      }}
                    />
                  </td>

                  {/* Actions cell */}
                  <td className="admin-edit-product-variant-table-cell admin-edit-product-variant-actions-cell">
                    <button
                      type="button"
                      className="admin-edit-product-variant-btn admin-edit-product-variant-btn-remove"
                      onClick={() => handleRemoveVariant(index)}
                    >
                      <i className="fas fa-trash-alt"></i> Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {variants.length === 0 && (
            <div className="admin-edit-product-variant-empty-state">
              <i className="fas fa-box-open fa-3x" style={{ color: '#ccc', marginBottom: '15px' }}></i>
              <p>No variants have been added yet. Click the button below to add a variant.</p>
            </div>
          )}
        </div>

        <div className="admin-edit-product-variant-add-container">
          <button
            type="button"
            className="admin-edit-product-variant-btn admin-edit-product-variant-btn-add"
            onClick={handleAddVariant}
          >
            <i className="fas fa-plus-circle"></i> Add New Variant
          </button>
        </div>

        <div className="admin-edit-product-variant-actions">
          <button
            type="submit"
            className="admin-edit-product-variant-btn admin-edit-product-variant-btn-save"
            disabled={isSaving || !areVariantsValid()}
          >
            {isSaving ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i> Save Variants
              </>
            )}
          </button>
        </div>

        <div className="admin-edit-product-variant-help-box">
          <div className="admin-edit-product-variant-help-header">
            <i className="fas fa-lightbulb"></i> Quick Tips
          </div>
          <ul className="admin-edit-product-variant-help-list">
            <li>Make sure all specification fields are completed for each variant</li>
            <li>You can add multiple variants with different specification combinations</li>
            <li>The product will be in pre-order state if price is not filled in for all variants</li>
          </ul>
        </div>
      </form>
    </div>
  );
}

export default AdminEditProductVariant;